// #### evaluate - a utility method primary used by {{condition}} helper. See Helpers' condition helper for more details of this method
// >Note: the expression shall not have a couple of outer parenthesis, like '(a && b)', it has to be 'a && b'.
// >Inner parenthesis are allowed, like 'a && (b || c)
//
// options is handlebars options
// $scope.key is assumed to be a var "key" set in the specified scope

module.exports = (expression, context, options) => {
  const { get } = require('../utils/getterSetter');

  // either 'str' or 10 like strings will return true
  const isPrimitive = (val) => {
    let retValue;

    if (Number.isNaN(Number(val))) {
      // Not a Number, check for pattern like "something"
      retValue = /^(['"]).*\1$/.test(val);
    } else {
      // Number
      retValue = true;
    }

    return retValue;
  };

  // Assumption str will pass isPrimitive test
  const getPrimitiveValue = (s) => {
    let str = s;
    if (/^(['"]).*\1$/.test(str)) {
      // convert '0' => String 0
      str = str.replace(/^(['"])(.*)\1$/, '$2');
    } else {
      // 0 => Number(0)
      str = Number(str);
    }

    return str;
  };

  const getContextValue = (gcvExpression, gcvContext = {}, gcvOptions = {}) => {
    let exp = gcvExpression;
    if (exp.indexOf('.') === -1) {
      // private variable
      if (exp.indexOf('@') === 0) {
        return gcvOptions.data ? gcvOptions.data[exp.substr(1)] : undefined;
      } else if (exp === 'this') {
        // keyword 'this'
        return gcvContext;
      }
      // [x] => x
      exp = exp.replace(/^\[(.+)\]$/, '$1').trim();
      return gcvContext[exp];
    }
    const parts = exp.split('.');
    let i = 0;
    const l = parts.length;
    let retValue = gcvContext;
    for (; i < l; i++) {
      retValue = getContextValue(parts[i], retValue);

      if (!retValue) {
        break;
      }
    }

    return retValue;
  };

  const parse = (s, sections = [], strings = []) => {
    // skip corresponding \' and \"
    const stringRegex = /(?:'(?:[^'\\]|\\')*')|(?:"(?:[^"\\]|\\")*")/;
    const sectionRegex = /\([^)(]*\)/;
    let match;
    const tree = {};
    const refill = function (t) {
      const text = t.replace(/---(\d+)---/, (m, p1) => sections[p1]).replace(/%%%(\d+)%%%/g, (m, p1) => strings[p1]);
      return text;
    };

    let str = s.trim();
    // change " ( a < b ) " to "a < b"
    if (str.match(/^\([^)(]+\)$/)) {
      str = str.replace(/^\((.*)\)$/, '$1').trim();
    }

    // eslint-disable-next-line no-cond-assign
    while ((match = stringRegex.exec(str)) !== null) {
      str = str.replace(stringRegex, `%%%${strings.length}%%%`);
      strings.push(match[0]);
    }

    // strings have been replaced, so no confusion of '(' or ')' inside strings
    while ((match = sectionRegex.exec(str)) !== null) { // eslint-disable-line no-cond-assign
      str = str.replace(sectionRegex, `---${sections.length}---`);
      sections.push(match[0]);
    }

    const parts = str.split(/\s+/);
    if (parts.length !== 1 && parts.length !== 3) {
      // eslint-disable-line no-cond-assign
      throw new Error(`invalid number of arguments ${parts}`);
    }

    // TODO: can we refill what ever is necessary NOT every thing
    tree.arg1 = refill(parts[0]);
    if (parts.length > 1) {
      // Need the previous sections, so pass the previous sections to parse
      tree.arg1 = parse(tree.arg1, sections, strings);
      tree.operator = parts[1];

      // Need the previous sections, so pass the previous sections to parse
      tree.arg2 = parse(refill(parts[2]), sections, strings);
    }

    return tree;
  };

  const calculate = (arg1, operator, arg2) => {
    /* jshint eqeqeq: false */
    // in handlebars {{this}} could be a Number or String, so we need to provide == and !=

    switch (operator) {
      case '||':
        return arg1 || arg2;
      case '&&':
        return arg1 && arg2;
      case '==':
        return arg1 == arg2; // eslint-disable-line eqeqeq
      case '===':
        return arg1 === arg2;
      case '!=':
        return arg1 != arg2; // eslint-disable-line eqeqeq
      case '!==':
        return arg1 !== arg2;
      case '<':
        return arg1 < arg2;
      case '<=':
        return arg1 <= arg2;
      case '>':
        return arg1 > arg2;
      case '>=':
        return arg1 >= arg2;
      case '%':
        return arg1 % arg2;
      case '+':
        return arg1 + arg2;
      case '-':
        return arg1 - arg2;
      case '/':
        return arg1 / arg2;
      case '*':
        return arg1 * arg2;
      default:
        throw new Error(`Operator '${operator}' is not implemented.`);
    }
  };

  const processTree = (tree, ptContext, ptOptions) => {
    const operator = tree.operator;
    let exp;

    if (!operator) {
      exp = tree.arg1;

      switch (exp) {
        case 'true':
        case true:
          return true;

        case 'false':
        case false:
          return false;

        case 'undefined':
        case undefined:
          return undefined;

        case 'null':
        case null:
          return null;

        default:
          // exp will always be of type string
          // either 'str' or 10 or x.y.[5] etc.
          // in above example 'str' and 10 will be considered primitive, string and number respectively
          if (isPrimitive(exp)) {
            return getPrimitiveValue(exp);
          } else if (typeof exp === 'string' && exp.indexOf('$') === 0) {
            // Assume $test is a variable that was saved with {{set}} helper
            return get(exp.substr(1), ptOptions);
          }
          return getContextValue(exp, ptContext, ptOptions);
      }
    }

    const arg1 = processTree(tree.arg1, ptContext, ptOptions);
    const arg2 = processTree(tree.arg2, ptContext, ptOptions);

    // TODO: work on short circuit
    return calculate(arg1, operator, arg2);
  };

  return processTree(parse(expression), context, options);
};
